import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Brand } from "../components/Brand";
import { ScoreGauge } from "../components/ScoreGauge";
import { ShieldCheck, Download, Share2, Star, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function PublicScorecard() {
  const { vid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/scorecard/${vid}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [vid]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: "Mock2Crack Verified Scorecard", url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    } catch (err) { console.debug("Share cancelled or unavailable", err); }
  };

  const downloadQR = () => {
    const a = document.createElement("a");
    a.href = data.qr; a.download = `mock2crack-${vid}.png`; a.click();
  };

  if (err) return (
    <div className="min-h-screen grid place-items-center bg-background px-5">
      <div className="text-center"><div className="font-heading text-2xl font-bold text-ink">Scorecard not found</div>
        <Link to="/" className="text-brand font-semibold text-sm mt-2 inline-block">← Back to home</Link></div>
    </div>
  );
  if (!data) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const comps = [
    ["technical", "Technical"], ["coding", "Coding"], ["communication", "Communication"],
    ["problem_solving", "Problem Solving"], ["behavioural", "Behavioral"], ["role_fit", "Role Fit"],
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="glass border-b border-slate-200 px-5 h-16 flex items-center justify-between sticky top-0 z-10">
        <Brand />
        <div className="flex gap-2">
          <button onClick={downloadQR} data-testid="download-qr" className="inline-flex items-center gap-1.5 text-xs font-bold text-ink bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-full transition-colors"><Download className="h-3.5 w-3.5" /> QR</button>
          <button onClick={() => window.print()} data-testid="download-scorecard" className="inline-flex items-center gap-1.5 text-xs font-bold text-ink bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-full transition-colors"><Download className="h-3.5 w-3.5" /> Scorecard</button>
          <button onClick={share} data-testid="share-scorecard" className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-brand hover:bg-brand-hover px-3.5 py-2 rounded-full transition-colors"><Share2 className="h-3.5 w-3.5" /> Share</button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 py-10 fade-up">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-lift overflow-hidden">
          {/* credential header */}
          <div className="relative text-white p-8 overflow-hidden" style={{ background: data.branding?.primary_color ? `linear-gradient(135deg, #0f172a, ${data.branding.primary_color})` : "#0f172a" }}>
            <div className="absolute inset-0 bg-grid opacity-20" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-emerald-300 bg-emerald-500/15 px-3 py-1.5 rounded-full mb-3">
                <ShieldCheck className="h-3.5 w-3.5" /> Verified Mock2Crack Interview
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Mock2Crack Verified Interview Credential</h1>
              <p className="text-slate-200 text-sm mt-1 max-w-md">{data.college_name ? `Issued in partnership with ${data.college_name}. ` : ""}Verified evidence of your Einstein Score.</p>
              <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
                <Brand light />
                {(data.branding?.logo || data.college_name) && (
                  <div className="flex items-center gap-3" data-testid="scorecard-college-brand">
                    {data.branding?.logo && <img src={data.branding.logo} alt="College logo" data-testid="scorecard-logo" className="h-12 w-12 object-contain rounded-lg bg-white/90 p-1" />}
                    {data.college_name && <span className="text-sm font-bold text-white">{data.college_name}</span>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* body */}
          <div className="p-8 grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-6">
              <div className="grid sm:grid-cols-2 gap-y-4 gap-x-8">
                {[
                  ["Candidate", data.candidate_name],
                  ["College", data.college_name || "Individual"],
                  ["Target Role", data.role_name],
                  ["Interview", data.type === "ai" ? "AI Interview" : "Expert Interview"],
                  ["Mode", `${data.mode_icon || "🎥"} ${data.mode_label || "AI Video Interview"}`],
                  ["Interview Date", data.date],
                  ["Verification ID", data.verification_id],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-ink-faint">{k}</div>
                    <div className={`font-semibold text-ink ${k === "Verification ID" ? "num text-brand" : ""}`}>{v}</div>
                  </div>
                ))}
              </div>

              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-ink-faint mb-2">Competency Scores</div>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {comps.map(([k, label]) => (
                    <div key={k}>
                      <div className="flex justify-between text-sm mb-1"><span className="text-ink-soft">{label}</span><span className="num font-bold text-ink">{data.competencies[k]}</span></div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-brand" style={{ width: `${data.competencies[k]}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-ink-faint mb-2">Key Strengths</div>
                  <ul className="space-y-1.5">{data.strengths.map((s, i) => <li key={i} className="flex items-center gap-2 text-sm text-ink-soft"><Star className="h-3.5 w-3.5 text-amber-400 fill-amber-300" /> {s}</li>)}</ul>
                </div>
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-ink-faint mb-2">Top Recommendations</div>
                  <ul className="space-y-1.5">{data.top_recommendations.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm text-ink-soft"><CheckCircle2 className="h-3.5 w-3.5 text-brand mt-0.5" /> {s}</li>)}</ul>
                </div>
              </div>
            </div>

            {/* side: score + QR */}
            <div className="space-y-5">
              <div className="rounded-2xl border border-slate-200 p-5 text-center">
                <div className="text-[11px] font-bold uppercase tracking-wider text-ink-faint mb-2">Einstein Score</div>
                <ScoreGauge value={data.overall} size={150} />
              </div>
              <div className="rounded-2xl border border-slate-200 p-5 text-center">
                <img src={data.qr} alt="Verification QR" className="w-full max-w-[160px] mx-auto rounded-lg" />
                <div className="text-[11px] text-ink-faint mt-2">Scan to verify · {data.verification_id}</div>
              </div>
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-ink-faint mt-5">This credential does not expose private candidate information. © 2026 Mock2Crack.</p>
      </div>
    </div>
  );
}
